import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { color } from '../../../app/theme';
import { TWD } from '../../../app/utils';
import IconChevronDown from '../../../assets/images/Icons/IconChevronDown';
import IconChevronUp from '../../../assets/images/Icons/IconChevronUp';
import IconDelete20 from '../../../assets/images/Icons/IconDelete20';
import IconEdit20 from '../../../assets/images/Icons/IconEdit20';
import IconX from '../../../assets/images/Icons/IconX';
import icHelpCircle from '../../../assets/images/svg/ic-help-circle.svg';
import Loading from '../../shared/Loading';
import Tooltip from '../../shared/Tooltip';

const useStyles = makeStyles(
  () => ({
    root: {
      width: '485px',
      height: 'calc(100vh - 80px)',
      position: 'absolute',
      top: '-45px',
      right: '-44px',
      border: `1px solid ${color.$9}`,
      overflow: 'auto',
      background: color.$7,
    },
    titleBlock: {
      padding: '20px 16px',
      backgroundColor: color.$7,
    },
    title: {
      fontSize: '17px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '17px',
      color: color.$13,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 10,
      '& > div': {
        display: 'flex',
        alignItems: 'center',
      },
    },
    totalAddWord: {
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '500',
      lineHeight: 'normal',
      color: color.$12,
    },
    word: {
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$13,
      borderRadius: 8,
      border: `1px solid ${color.$10}`,
      padding: '2px 6px',
    },
    rowBlock: {
      borderTop: `1px solid ${color.$9}`,
    },
    row: {
      display: 'flex',
      alignItems: 'start',
      padding: '16px',
      borderBottom: `1px solid ${color.$9}`,
      background: color.$23,
    },
    top: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$10,
      marginBottom: 8,
    },
    updatedAt: {
      display: 'flex',
      alignItems: 'center',
      '& > svg:first-child': {
        marginLeft: 12,
      },
      '& > svg:last-child': {
        marginLeft: 4,
      },
    },
    icon: {
      cursor: 'pointer',
    },
    text: {
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$13,
      marginBottom: 8,
      overflowWrap: 'break-word',
      whiteSpace: 'pre-wrap',
    },
    prevText: {
      color: color.$10,
      marginBottom: 8,
    },
    more: {
      cursor: 'pointer',
      padding: 16,
      background: color.$7,
      borderBottom: `1px solid ${color.$9}`,
      fontSize: '15px',
      fontStyle: 'normal',
      fontWeight: '500',
      lineHeight: 'normal',
      color: color.$2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& > div': {
        flex: 'none',
        marginLeft: 4,
      },
    },
    isShowAll: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '500',
      lineHeight: '13px',
      color: color.$2,
      cursor: 'pointer',
    },
    dfcsb: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  }),
  {
    name: 'CorrectionRecords',
  },
);

function CorrectionRecordsRow({
  data,
  onOpenCorrectionModal,
  onDelete,
  isShowAll,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isHide, setIsHide] = useState(true);
  const { pathKey } = useSelector(store => store.aiBot);

  const {
    id,
    lastEditAt,
    editor,
    question,
    originalAnswer,
    newAnswer,
    newSourceUrl,
    wordCount,
  } = data || {};

  useEffect(() => {
    setIsHide(!isShowAll);
  }, [isShowAll]);

  return (
    <div className={classes.row}>
      <div style={{ width: '100%' }}>
        <div className={classes.top}>
          <div className={classes.updatedAt}>
            {moment(lastEditAt).format('YYYY-MM-DD HH:mm:ss')}
            <IconEdit20
              className={classes.icon}
              id={`icon-edit-${id}`}
              hoverColor={color.$1}
              onClick={() => onOpenCorrectionModal(data)}
            />
            <IconDelete20
              className={classes.icon}
              id={`icon-delete-${id}`}
              hoverColor={color.$1}
              onClick={() => onDelete(id)}
            />
          </div>
          <div>
            {`${editor?.firstName} ${editor?.lastName}`}
            <span style={{ marginLeft: 4 }}>
              {t(`${pathKey}:correctionRecordsSidebar.editor`)}
            </span>
          </div>
        </div>
        <div className={classes.text}>
          {t(`${pathKey}:correctionRecordsSidebar.question`)}
          {question}
        </div>
        {!isHide && (
          <>
            <div className={classes.text}>
              {t(`${pathKey}:correctionRecordsSidebar.response`)}
              <div>{newAnswer}</div>
            </div>
            {newSourceUrl && (
              <div className={classes.text}>
                {t(`${pathKey}:correctionRecordsSidebar.sourceUrl`)}
                <div>{newSourceUrl}</div>
              </div>
            )}
            <div className={classnames(classes.text, classes.prevText)}>
              {t(`${pathKey}:correctionRecordsSidebar.prevResponse`)}
              <div>{originalAnswer}</div>
            </div>
          </>
        )}
        <div className={classes.totalAddWord}>
          {t(`${pathKey}:correctionRecordsSidebar.addWord`)}
          <span className={classes.word}>{TWD(wordCount, false).format()}</span>
        </div>
      </div>
      {isHide ? (
        <IconChevronDown
          onClick={() => {
            setIsHide(!isHide);
          }}
          style={{ marginLeft: 8, cursor: 'pointer' }}
          id={`${id}-show-hide`}
        />
      ) : (
        <IconChevronUp
          onClick={() => {
            setIsHide(!isHide);
          }}
          style={{ marginLeft: 8, cursor: 'pointer' }}
          id={`${id}-show-hide`}
        />
      )}
    </div>
  );
}

function CorrectionRecords({
  list,
  total,
  quota,
  onMore,
  onClose,
  onDelete,
  onOpenCorrectionModal,
  isLoading,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isShowAll, setIsShowAll] = useState(false);
  const [isShowMore, setIsShowMore] = useState(true);
  const { pathKey } = useSelector(store => store.aiBot);

  const handleMore = async () => {
    const response = await onMore();
    setIsShowMore(!!response);
  };

  useEffect(() => {
    setIsShowAll(false);
    setIsShowMore(true);
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.titleBlock}>
        <div className={classes.title}>
          <div>
            {t(`${pathKey}:correctionRecordsSidebar.title`)}
            <Tooltip
              light
              placement="top"
              title={t(`${pathKey}:correctionRecordsSidebar.tooltip`)}
            >
              <img style={{ marginLeft: 4 }} src={icHelpCircle} alt="alert" />
            </Tooltip>
          </div>
          <div style={{ cursor: 'pointer' }} onClick={onClose}>
            <IconX
              id="CorrectionRecords"
              color={color.$10}
              hoverColor={color.$10}
            />
          </div>
        </div>
        <div className={classes.dfcsb}>
          <div className={classes.totalAddWord}>
            {t(`${pathKey}:basicSettingContainer.totalTuningCount`)}
            <span className={classes.word}>{`${TWD(
              total,
              false,
            ).format()}/${TWD(quota, false).format()}`}</span>
          </div>
          <div
            className={classes.isShowAll}
            onClick={() => setIsShowAll(!isShowAll)}
          >
            {isShowAll ? (
              <>
                {t(`${pathKey}:correctionRecordsSidebar.allShow`)}
                <IconChevronUp
                  style={{ marginLeft: 6 }}
                  color={color.$2}
                  hoverColor={color.$2}
                  id="CorrectionRecords-show-hide"
                />
              </>
            ) : (
              <>
                {t(`${pathKey}:correctionRecordsSidebar.allHide`)}
                <IconChevronDown
                  style={{ marginLeft: 6 }}
                  color={color.$2}
                  hoverColor={color.$2}
                  id="CorrectionRecords-show-hide"
                />
              </>
            )}
          </div>
        </div>
      </div>
      <div className={classes.rowBlock}>
        {list.map(l => (
          <CorrectionRecordsRow
            key={`CorrectionRecordsRow-${l?.id}`}
            data={l}
            onOpenCorrectionModal={onOpenCorrectionModal}
            onDelete={onDelete}
            isShowAll={isShowAll}
          />
        ))}
        {isShowMore && (
          <div className={classes.more} onClick={handleMore}>
            {t(`${pathKey}:correctionRecordsSidebar.more`)}
            {isLoading && <Loading topic="green" />}
          </div>
        )}
      </div>
    </div>
  );
}

export default CorrectionRecords;
